import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { getPlans } from '../services/apiPlans';
import { getFixtures } from '../services/apiFixtures';
import { useChannel } from '../hooks/useChannel';
import { useBanners } from '../features/hero/useBanners';
import SpinnerFullPage from './SpinnerFullPage';
import ErrorFallback from './ErrorFallback';
import HeadTag from './HeadTag';
import ReactGA from 'react-ga4';

// WHY: Define interface for ChannelThemeLoader props
// Loads channel configuration and applies theme CSS variables
// Prefetches fixtures and plans data for performance
// Initializes Google Analytics with channel-specific tag
// Shows spinner during loading, error fallback on failure
export interface ChannelThemeLoaderProps {
  children: React.ReactNode; // App content to render when theme loaded
}

function ChannelThemeLoader({ children }: ChannelThemeLoaderProps) {
  // WHY: Load the channel theme configuration
  const {
    isLoading: isLoadingChannel,
    error: channelError,
    channel,
  } = useChannel();

  const { isLoading: isLoadingBanners } = useBanners();

  const metaConfig = channel?.meta;
  const cookieTag = metaConfig?.cookie_tag;
  const gaTag = metaConfig?.ga_tag;

  // WHY: Initialize Google Analytics when GA tag is available
  useEffect(() => {
    if (gaTag) {
      ReactGA.initialize(gaTag.trim());
    }
  }, [gaTag]);

  // WHY: Commented out old domain validation logic
  // Preserved in case feature needs to be re-enabled
  // if (!isLoadingDisplayConfig && errorSectionsDisplayConfig && settings)
  //   return <DomainNotFound />;

  // WHY: Prefetch data for better performance
  const queryClient = useQueryClient();

  useEffect(() => {
    function prefetchData() {
      queryClient.prefetchQuery({
        queryKey: ['fixtures'],
        queryFn: getFixtures,
      });
      queryClient.prefetchQuery({
        queryKey: ['plans'],
        queryFn: getPlans,
      });
    }

    if (!isLoadingBanners) {
      prefetchData();
    }
  }, [queryClient, isLoadingBanners]);

  // WHY: Apply theme color to player container
  // Sets CSS variable for video player theming
  useEffect(() => {
    if (!channel) return;
    const playerContainer = document.getElementById('playercontainer');
    if (playerContainer) {
      playerContainer.style.setProperty(
        '--theme-action-primary',
        channel.theme_color || channel.theme_action_primary
      );
    }
  }, [channel]);

  // WHY: Show loading spinner while channel data loads
  if (isLoadingChannel) return <SpinnerFullPage />;

  // WHY: Show error page if channel data fails to load
  if (channelError) return <ErrorFallback />;

  // WHY: Extract theme variables from channel configuration
  const {
    theme_action_primary,
    theme_action_secondary,
    theme_assets_recolour,
    theme_background_primary,
    theme_background_secondary,
    theme_background_tertiary,
    theme_font,
    theme_color,
    theme_text_color,
    name,
    logo_url,
  } = channel!; // Non-null assertion safe here after loading checks

  // WHY: Build theme object with CSS custom properties
  // Applied as inline styles to main element for global theming
  const theme = {
    '--theme-action-primary': theme_color || theme_action_primary,
    '--theme-action-secondary': theme_action_secondary,
    '--theme-assets-recolour': theme_assets_recolour,
    '--theme-background-primary': theme_background_primary,
    '--theme-background-secondary': theme_background_secondary,
    '--theme-background-tertiary': theme_background_tertiary,
    '--theme-font-family': theme_font || 'Inter',
    '--theme-text-color': theme_text_color,
  } as React.CSSProperties;

  // WHY: Commented out old body style approach
  // Now using main element inline styles instead
  // document.body.style = `--theme-action-primary: ${
  //   theme_color || theme_action_primary
  // }`;

  return (
    <>
      <HeadTag title={name} favIcon={logo_url} cookieScriptId={cookieTag} />
      <main style={theme} id="home" aria-label="Home">
        {children}
      </main>
    </>
  );
}

export default ChannelThemeLoader;
